import { createContext, useContext, useRef, useState } from 'react';
import { runSync } from '../utils/extBridge';
import { useToast } from '../components/Toast';
import { useNotifications } from './NotificationContext';
import { useAuth } from './AuthContext';
import client from '../api/client';

const SyncContext = createContext(null);

// Nhãn hiển thị cho từng hành động đồng bộ.
export const SYNC_LABELS = {
  orders: 'Đẩy đơn hàng',
  deposits: 'Đẩy tỷ lệ cọc',
  customers: 'Đẩy danh sách KH',
  revenues: 'Đẩy báo cáo doanh thu',
  expected: 'Doanh thu dự kiến',
  totalOrders: 'Tổng đơn hàng',
  backfill: 'Backfill KPI (từ đầu năm)',
  weekly: 'Tỷ lệ cọc tháng này',
  depositsMonthly: 'Backfill tỷ lệ cọc 6 tháng',
  details: 'Lấy chi tiết KH',
  debts: 'Quét công nợ',
  businessWeekly: 'Quét doanh thu tuần',
};

export function SyncProvider({ children }) {
  const toast = useToast();
  const { addNotification } = useNotifications();
  const { me } = useAuth();
  const [running, setRunning] = useState(null); // actionId đang chạy (toàn cục)
  const [progress, setProgress] = useState('');
  const [lastResult, setLastResult] = useState(null); // { actionId, ok, message }
  const abortRef = useRef(null);

  async function startSync(actionId, params = {}) {
    if (running) {
      toast('Đang có một tác vụ đồng bộ chạy, vui lòng đợi xong', 'warn');
      return { ok: false, error: 'busy' };
    }
    const ac = new AbortController();
    abortRef.current = ac;
    setRunning(actionId);
    setProgress('Đang chạy…');
    setLastResult(null);
    // Kèm NGƯỜI QUÉT (tài khoản đang đăng nhập): extension gửi scanned_by về server -> đợt quét
    // hiện tên người quét + TP/NV chỉ quét được KH nhóm mình (server tự lọc).
    const agent = me ? { id: me.id || me._id, name: me.name } : null;
    const res = await runSync(actionId, { ...params, agent }, (text) => setProgress(text), ac.signal);
    abortRef.current = null;
    setRunning(null);
    setProgress('');
    if (res.cancelled) {
      toast('Đã dừng tác vụ', 'warn');
      return res;
    }
    setLastResult({ actionId, ok: res.ok, message: res.ok ? res.message : res.error });
    // Ghi lần cuối chạy thành công (hiển thị ở màn Đồng bộ).
    if (res.ok) client.post('/sync-jobs/mark', { action: actionId }).catch(() => {});
    const msg = res.ok ? res.message || 'Đồng bộ xong' : res.error || 'Có lỗi xảy ra';
    toast(msg, res.ok ? 'ok' : 'danger');
    addNotification(`[${SYNC_LABELS[actionId] || actionId}] ${msg}`, res.ok ? 'ok' : 'danger');
    return res;
  }

  // Dừng tác vụ đồng bộ đang chạy.
  function stopSync() {
    abortRef.current?.abort();
  }

  return (
    <SyncContext.Provider value={{ running, progress, lastResult, startSync, stopSync }}>
      {children}
    </SyncContext.Provider>
  );
}

export function useSync() {
  return useContext(SyncContext);
}
